import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FieldValues, SubmitErrorHandler, useForm as useHookForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

// TODO: improve typing, the hook form uses object with inputNames as keys
//       while the settings form uses variable names
export interface FormConfig {
  initialData: Record<string, any>
  load: () => Promise<Record<string, any>>
  validate: () => Promise<void>
  save: (data: Record<string, any>) => Promise<void>
  autoLoad?: boolean
  schema?: yup.ObjectSchema<any>
}

export const useForm = ({ initialData, load, save, autoLoad = true, schema }: FormConfig) => {
  const canSetState = useRef(true) // Guard for setState operations
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isFailed, setIsFailed] = useState(false)
  const [defaultValues, setDefaultValues] = useState(initialData)
  const [isReady, setIsReady] = useState(false)

  const hookForm = useHookForm({
    defaultValues: { ...(JSON.parse(JSON.stringify(initialData)) as any) },
    reValidateMode: 'onSubmit',
    resolver: schema ? yupResolver(schema) : undefined
  })

  const saveForm = useCallback(
    async (data: Record<string, any>) => {
      try {
        if (canSetState.current) {
          setIsSaving(true)
        }
        const dirtyFields: Record<string, any> = {}
        Object.entries(hookForm.formState.dirtyFields).forEach(([k, v]) => {
          if (v) {
            dirtyFields[k] = data[k]
          }
        })
        await save(dirtyFields)
        hookForm.clearErrors()
        hookForm.reset(hookForm.getValues())
      } finally {
        if (canSetState.current) {
          setIsSaving(false)
        }
      }
    },
    [hookForm, save]
  )

  const handleInvalidForm: SubmitErrorHandler<FieldValues> = useCallback(
    async fieldErrors => {
      // Let form be saved if only non-dirty fields have errors, so existing invalid data on the UI
      // won't block updating the settings partially
      const dirtyFieldNames = Object.keys(hookForm.formState.dirtyFields)
      if (Object.keys(fieldErrors).some(fieldName => dirtyFieldNames.includes(fieldName))) {
        return
      }
      await saveForm(hookForm.getValues())
    },
    [hookForm, saveForm]
  )

  const loadForm = useCallback(async () => {
    try {
      if (canSetState.current) {
        setIsLoading(true)
      }
      const values = await load()
      if (canSetState.current) {
        setDefaultValues(values)
      }
      hookForm.reset(values)
      hookForm.clearErrors()
    } finally {
      if (canSetState.current) {
        setIsLoading(false)
      }
    }
  }, [hookForm, load])

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false // Hook shouldn't call react setState when guard is false
    }
  }, [])

  useEffect(() => {
    if (!autoLoad) {
      setIsReady(true)
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-extra-semi
    ;(async () => {
      try {
        await loadForm()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
        if (canSetState.current) {
          setIsFailed(true)
        }
      } finally {
        setIsReady(true)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return useMemo(
    () => ({
      ...hookForm,
      load: loadForm,
      save: hookForm.handleSubmit(saveForm, handleInvalidForm),
      isLoading,
      isSaving,
      isFailed,
      defaultValues,
      isReady
    }),
    [hookForm, loadForm, saveForm, handleInvalidForm, isLoading, isSaving, isFailed, defaultValues, isReady]
  )
}

export type IForm = ReturnType<typeof useForm>
